/*  iceberg-ui  */
import { Box, Button, Card, Flex } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { FormLabel, Grid, TextField, Typography } from '@mui/material';

/* tanstack */
import { CellContext } from '@tanstack/react-table';
import { PageTitle } from 'application/Shared/Components/App';
import {
    DateFormatCell,
    IconCell,
    IconCellType,
    NumberCell,
    StringCell,
    activeExpiredDate,
    dateFilter,
    dateFilterEndDate,
    lastXDays
} from 'application/Shared/Components/Cells';

/*  Shared  */
import { DateFormat } from 'application/Shared/Components/Core';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { CSVColumn, Toggle } from 'application/Shared/Components/Table/types';
import { filterFns } from 'application/Shared/Components/TableColumnFilters';
import { sortByTimestamp } from 'application/Shared/Components/TableSortFunctions';
import { DarkTooltip, TooltipQ } from 'application/Shared/Components/Tooltips';
import { TABLENAMES } from 'application/Shared/Constants';
import {
    Notify,
    Response,
    Route,
    Transition
} from 'application/Shared/Helpers';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';
import { debug } from 'application/Shared/Utils';
import { Tenant } from 'application/Shared/types/tenant';

/*  NPM  */
import axios from 'axios';
import { TenantStatsMapInterface } from 'hooks/dataAccess/GlobalAdmin/tenantStat';
import { TFunction } from 'i18next';

/*  React  */
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
    GlobalTenantTableContextProvider,
    useGlobalTenantTableContext
} from './GlobalTenantTableContext';

const createPartnerTenant = async ({
    partnerName
}: {
    partnerName: string;
}) => {
    try {
        const response = await axios.post('/tenant', {
            identityTenantId: '',
            identityTenantName: partnerName,
            identityTenantType: '',
            name: partnerName,
            partner: true
        });
        if (!Response.hasData(response)) {
            debug.e('No Create Tenant Response', response);
            return false;
        }

        return Response.getId(response);
    } catch (e) {
        debug.e('Create Partner Tenant Error:', e);
        return false;
    }
};

const createPartner = async ({
    adminEmail,
    partnerName,
    setOpenCreateTenant,
    setRerender,
    t
}: {
    adminEmail: string;
    partnerName: string;
    setOpenCreateTenant: Dispatch<SetStateAction<boolean>>;
    setRerender: Dispatch<SetStateAction<boolean>> | undefined;
    t: TFunction<'translation', undefined>;
}) => {
    const tenantId = await createPartnerTenant({
        partnerName
    });
    if (!tenantId) {
        Notify.e(t('GlobalAdmin.Tenant.unabletoCreatePartner'));
        setOpenCreateTenant(false);
        return;
    }

    try {
        await axios.post('/user', {
            admin: true,
            email: adminEmail,
            firstName: adminEmail,
            lastName: '',
            tenantId,
            tenantUserId: adminEmail
        });
        Notify.s(t('GlobalAdmin.Tenant.partnerCreatedSuccessfully'));
        if (setRerender) setRerender(true);
    } catch (e) {
        Notify.e(
            t('GlobalAdmin.Tenant.unabletoCreateAdminUserUnderNewPartner')
        );
        debug.e(e);
    } finally {
        setOpenCreateTenant(false);
    }
};

const CreateTenantButton = () => {
    const { t } = useTranslation();
    const { setOpenCreateTenant } = useGlobalTenantTableContext();

    return (
        <Box
            sx={{
                position: 'absolute',
                right: '16px',
                top: '20px'
            }}
        >
            <Button
                onClick={() => setOpenCreateTenant(true)}
                data-trackid="globalAdmin_tenant_createPartner"
            >
                {t('GlobalAdmin.Tenant.createPartner')}
            </Button>
        </Box>
    );
};

const CreateTenantModal = () => {
    const { t } = useTranslation();
    const { openCreateTenant, setOpenCreateTenant } =
        useGlobalTenantTableContext();
    const { setRerender } = useManagedTableV2Context();

    const [partnerName, setPartnerName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');

    return (
        <Modal
            open={openCreateTenant}
            TransitionComponent={Transition}
            onClose={() => {
                setOpenCreateTenant(false);
            }}
            title={t('GlobalAdmin.Tenant.createPartner')}
        >
            <ModalBody style={{ padding: '32px' }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormLabel component="label">
                            {t('Common.partner_name')}
                        </FormLabel>
                        <TextField
                            autoFocus
                            onChange={(e) => setPartnerName(e.target.value)}
                            fullWidth
                            value={partnerName}
                            placeholder={t('Common.brand_name')}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormLabel component="label">
                            {t('Common.admin_email')}
                        </FormLabel>
                        <TextField
                            fullWidth
                            onChange={(e) => setAdminEmail(e.target.value)}
                            value={adminEmail}
                            placeholder="support@auvik.com"
                            type="email"
                        />
                    </Grid>
                </Grid>
            </ModalBody>
            <ModalFooter>
                <Button
                    data-trackid="globalAdmin_tenant_createPartner_cancel"
                    onClick={() => setOpenCreateTenant(false)}
                    variant="secondary"
                >
                    {t('Common.cancel')}
                </Button>
                <Button
                    data-trackid="globalAdmin_tenant_createPartner_create"
                    onClick={() =>
                        createPartner({
                            adminEmail,
                            partnerName,
                            setOpenCreateTenant,
                            setRerender,
                            t
                        })
                    }
                >
                    {t('Common.create')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const ActionsCell = ({ row }: CellContext<Tenant, undefined>) => {
    const { t } = useTranslation();

    const { id, partner, partnerTenant, settings } = row.original;

    const hasSupportAccess = settings?.enableAuvikSupportAccess !== false;

    const clientTenantId = partner ? null : id;
    const partnerTenantId = partner ? id : (partnerTenant as Tenant)?.id;
    const routeToPartner = Route.buildBasePartnerRoute({
        partnerId: partnerTenantId
    });
    const routeToClient = clientTenantId
        ? Route.buildBaseAdminRoute({
              clientId: clientTenantId,
              partnerId: partnerTenantId
          })
        : routeToPartner;

    const options = [
        {
            key: 'GlobalAdmin.Tenant.EditTenant',
            label: t('GlobalAdmin.Tenant.EditTenant'),
            link: {
                'data-testid': 'editTenantLink',
                style: { color: 'inherit' },
                to: `/globaladmin/tenant/${id}/edit`
            },
            value: 'edit'
        },
        {
            disabled: !hasSupportAccess,
            hide: !!partner,
            key: 'GlobalAdmin.Tenant.ViewClient',
            label: t('GlobalAdmin.Tenant.ViewClient'),
            link: {
                'data-testid': 'viewClientLink',
                style: { color: 'inherit' },
                to: routeToClient
            },
            // TODO: Figure out the tooltip. Since the row is disabled when the tooltip is shown, you can't actually view the tooltip.
            tooltip: hasSupportAccess
                ? undefined
                : t('GlobalAdmin.Tenant.revokedRemoteAccess'),
            value: 'viewClient'
        },
        {
            disabled: !hasSupportAccess,
            key: 'GlobalAdmin.Tenant.ViewPartner',
            label: t('GlobalAdmin.Tenant.ViewPartner'),
            link: {
                'data-testid': 'viewPartnerLink',
                style: { color: 'inherit' },
                to: routeToPartner
            },
            // TODO: Figure out the tooltip. Since the row is disabled when the tooltip is shown, you can't actually view the tooltip.
            tooltip: hasSupportAccess
                ? undefined
                : t('GlobalAdmin.Tenant.revokedRemoteAccess'),
            value: 'viewPartner'
        }
    ];

    return (
        <Flex alignItems="center" justifyContent="flex-end">
            <KebabMenu
                options={options}
                onClick={() => {}}
                data-testid="globalAppsActions"
            />
        </Flex>
    );
};

const AdminInfoCell = ({ row }: CellContext<Tenant, unknown>) => {
    const { adminUser } = row.original;

    return adminUser ? (
        <>
            <Box>
                <Typography color="textSecondary" fontWeight={600}>
                    {adminUser.firstName} {adminUser.lastName}
                </Typography>
            </Box>
            <Box>
                <small>{adminUser.email}</small>
            </Box>
        </>
    ) : null;
};

const iconCellProps = (
    t: TFunction<'translation', undefined>
): IconCellType => ({
    size: 11,
    src: 'DisabledPlay',
    tooltipText: t('Common.activity_blocked'),
    variant: 'avatar'
});

const HubTypeCell = ({ row }: CellContext<Tenant, unknown>) => {
    const { settings } = row.original;
    const hubTypeText = settings?.hubType || 'None';
    return hubTypeText;
};

const ParnerInfoCell = ({ row }: CellContext<Tenant, unknown>) => {
    const { t } = useTranslation();

    const { anm, id, partner, partnerTenant } = row.original;

    const partnerTenantId = partner ? id : (partnerTenant as Tenant).id;

    return (
        <>
            {anm && partner && (
                <Typography color="text.tertiary" sx={{ fontWeight: 600 }}>
                    {t('GlobalAdmin.Tenant.created_via_anm')}
                </Typography>
            )}
            {!partner && (
                <Box width="100%">
                    <Link
                        className="link underline"
                        to={`/globaladmin/tenant/${partnerTenantId}/details`}
                        data-trackid="globalAdmin_tenants_partner_routeTo"
                    >
                        <Typography variant="span" sx={{ fontWeight: 600 }}>
                            {(partnerTenant as Tenant)?.name || t('Common.n/a')}
                        </Typography>
                    </Link>
                    <Typography sx={{ fontSize: '9px' }}>
                        {(partnerTenant as Tenant).id}
                    </Typography>
                </Box>
            )}
        </>
    );
};

const TenantNameCell = ({ row }: CellContext<Tenant, unknown>) => {
    const { t } = useTranslation();

    const { blocked, id, name, settings } = row.original;

    const { brandName } = settings || {};

    const hasSupportAccess = settings?.enableAuvikSupportAccess !== false;

    let tooltipText;

    if (!hasSupportAccess) {
        tooltipText = t('GlobalAdmin.Tenant.revokedRemoteAccess');
    }

    const tenantName = brandName || name;

    return (
        <Grid container>
            <Grid item xs={12} display="flex" alignItems="flex-start">
                {blocked && (
                    <TooltipQ
                        text={t('Common.activity_blocked')}
                        content={
                            <IconCell
                                condition={blocked}
                                iconCellProps={iconCellProps(t)}
                            />
                        }
                    />
                )}
                <DarkTooltip arrow title={tooltipText}>
                    <Box>
                        <Link
                            className="link underline"
                            data-testid={`partnerClientMainLink${tenantName}`}
                            data-trackid="globalAdmin_tenants_client_routeTo"
                            to={`/globaladmin/tenant/${id}/details`}
                        >
                            <Typography variant="span" sx={{ fontWeight: 600 }}>
                                {tenantName}
                            </Typography>
                        </Link>
                        {brandName && (
                            <small>
                                <br />
                                {name}
                            </small>
                        )}
                    </Box>
                </DarkTooltip>
            </Grid>
        </Grid>
    );
};

const TrialEndDateCell = ({ row }: CellContext<Tenant, unknown>) => {
    const { partner, settings } = row.original;
    const isDateNotNull = !!settings?.trialEndDate;
    return partner && isDateNotNull ? (
        <DateFormat date={settings?.trialEndDate as Date} />
    ) : (
        '-'
    );
};

const columns = (t: TFunction<'translation', undefined>) => [
    {
        accessorKey: 'name',
        cell: TenantNameCell,
        enableColumnFilter: true,
        filterFn: 'arrIncludesSome',
        header: Header,
        id: 'tenantName',
        name: 'GlobalAdmin.Tenant.name',
        size: 200,
        testId: 'tenantName'
    },
    {
        accessorKey: 'id',
        cell: StringCell,
        enableColumnFilter: true,
        filterFn: 'arrIncludesSome',
        header: Header,
        id: 'id',
        name: 'Common.id',
        testId: 'id'
    },
    {
        accessorKey: 'region',
        cell: StringCell,
        enableColumnFilter: true,
        filterFn: 'arrIncludesSome',
        header: Header,
        id: 'region',
        name: 'Common.region',
        testId: 'region'
    },
    {
        accessorKey: 'tenantStats.totalClients',
        cell: NumberCell,
        enableColumnFilter: false,
        header: Header,
        id: 'totalClients',
        name: 'Common.client_count',
        testId: 'totalClients'
    },
    {
        accessorKey: 'tenantStats.totalPartnerUsers',
        cell: NumberCell,
        enableColumnFilter: false,
        header: Header,
        id: 'totalPartnerUsers',
        name: 'Common.user_count',
        testId: 'totalPartnerUsers'
    },
    {
        accessorKey: 'tenantStats.totalPartnerDevices',
        cell: NumberCell,
        enableColumnFilter: false,
        header: Header,
        id: 'totalPartnerDevices',
        name: 'Common.device_count',
        testId: 'totalPartnerDevices'
    },
    {
        accessorKey: 'dateCreated',
        cell: DateFormatCell,
        filterFn: lastXDays,
        header: Header,
        id: 'dateCreated',
        meta: { filter: dateFilter },
        name: 'Common.dateAdded',
        sortingFn: sortByTimestamp,
        testId: 'dateCreated'
    },
    {
        accessorFn: (row: Tenant) => row?.settings?.hubType || 'None',
        cell: HubTypeCell,
        enableColumnFilter: true,
        enableSorting: false,
        filterFn: filterFns.arrIncludesSomeWithNulls,
        header: Header,
        id: 'hubtype',
        name: 'GlobalAdmin.Tenant.hubType',
        testId: 'hubtype'
    },
    {
        accessorFn: (row: Tenant) => row?.settings?.trialEndDate,
        cell: TrialEndDateCell,
        filterFn: activeExpiredDate,
        header: Header,
        id: 'trialEndDate',
        meta: { filter: dateFilterEndDate },
        name: 'GlobalAdmin.Tenant.TrialEndDate.label',
        sortingFn: sortByTimestamp,
        testId: 'trialEndDate'
    },
    {
        accessorFn: (row: Tenant) => row?.adminUser?.email,
        cell: AdminInfoCell,
        enableColumnFilter: false,
        header: Header,
        id: 'adminUser',
        name: 'Common.admin_user',
        testId: 'adminUser'
    },
    {
        accessorKey: 'partner',
        cell: ParnerInfoCell,
        enableColumnFilter: false,
        header: Header,
        id: 'partner',
        name: 'Common.partner',
        testId: 'partner'
    },
    {
        accessorKey: 'blocked',
        cell: ({ cell }: CellContext<Tenant, unknown>) =>
            cell.getValue() ? t('Common.yes') : t('Common.no'),
        enableColumnFilter: true,
        filterFn: filterFns.arrIncludesBooleans,
        header: Header,
        id: 'blocked',
        name: 'Common.blocked',
        size: 10,
        testId: 'blocked'
    },
    {
        accessorKey: 'actions',
        cell: ActionsCell,
        enableColumnFilter: false,
        enableSorting: false,
        header: Header,
        id: 'actions',
        meta: {
            align: 'right',
            header: { justifyContent: 'flex-end' }
        },
        name: 'Common.actions',
        size: 50,
        testId: 'actions'
    }
];

const csvFields = (t: TFunction<'translation', undefined>): CSVColumn[] => [
    { key: 'id', label: t('Common.id') },
    { key: 'region', label: t('Common.region') },
    {
        key: 'tenantStats.totalClients',
        label: t('Common.client_count')
    },
    {
        key: 'tenantStats.totalPartnerUsers',
        label: t('Common.user_count')
    },
    {
        key: 'tenantStats.totalPartnerDevices',
        label: t('Common.device_count')
    },
    { key: 'name', label: t('Common.name') },
    { key: 'adminUser.email', label: t('CSV.admin_user_email') },
    {
        key: 'adminUser.firstName',
        label: t('CSV.admin_user_first_name')
    },
    { key: 'adminUser.lastName', label: t('CSV.admin_user_last_name') },
    {
        key: 'dateCreated',
        label: t('Common.dateAdded')
    },
    {
        key: 'partner',
        label: t('GlobalAdmin.Tenant.isPartner'),
        transform: 'bool_YN'
    },
    { key: 'settings.hubType', label: t('GlobalAdmin.Tenant.hubType') },
    {
        key: 'settings.trialEndDate',
        label: t('GlobalAdmin.Tenant.TrialEndDate.label')
    },
    { key: 'anm', label: t('Common.anm'), transform: 'bool_YN' },
    { key: 'blocked', label: t('Common.blocked'), transform: 'bool_YN' }
];

// interface AugmentedRows extends Tenant, TenantStatsMapInterface
type AugmentedRows = Tenant & TenantStatsMapInterface;

const handlePreprocessRows = ({
    rows,
    tenantStats
}: {
    rows: AugmentedRows[];
    tenantStats: TenantStatsMapInterface | undefined;
}) => {
    if (!rows?.length) {
        return rows;
    }

    rows.forEach((row) => {
        // mutate the row to include tenantStats for filtering
        if (!row.tenantStats) {
            // eslint-disable-next-line no-param-reassign
            row.tenantStats = tenantStats?.[row.id]
                ? { ...tenantStats[row.id] }
                : {
                      totalClients: 0,
                      totalPartnerDevices: 0,
                      totalPartnerUsers: 0
                  };
        }
    });

    return rows;
};

export const GlobalTenantPage = () => {
    const { t } = useTranslation();

    const {
        baseParameters,
        openCreateTenant,
        tenantStats,
        loadingTenantStats
    } = useGlobalTenantTableContext();

    const toggles: Toggle[] = useMemo(
        () => [
            {
                defaultEnabled: true,
                key: 'partner',
                parameter: {
                    field: 'partner',
                    value: true
                },
                toggleText: t('GlobalAdmin.Tenant.partnersOnly')
            }
        ],
        [t]
    );

    if (loadingTenantStats) {
        return null;
    }

    const columnPinning = {
        left: ['tenantName'],
        right: ['actions']
    };

    return (
        <ManagedTableV2ContextProvider
            baseParameters={baseParameters}
            columns={columns(t)}
            getRowId="id"
            ignoreSearchParams
            initialState={{
                columnPinning
            }}
            mode="client"
            preprocessRows={(rows: AugmentedRows[]) =>
                handlePreprocessRows({ rows, tenantStats })
            }
            rowsApi="/tenant"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            sortDirection="ASC"
            sortKey="tenantName"
            tableName={TABLENAMES.GLOBAL_TENANTS}
            toggles={toggles}
        >
            <PageTitle title={t('GlobalAdmin.Tenant.tenants')} />
            <Card sx={{ mt: '24px', p: 0, position: 'relative' }}>
                <CreateTenantButton />
                {openCreateTenant && <CreateTenantModal />}
                <ManagedTableV2UI
                    csvExport
                    csvExportFields={csvFields(t)}
                    csvExportFileName="license_user_breakdown"
                    renderFooter
                />
            </Card>
        </ManagedTableV2ContextProvider>
    );
};

export const GlobalTenant = () => (
    <GlobalTenantTableContextProvider>
        <GlobalTenantPage />
    </GlobalTenantTableContextProvider>
);
